import { forwardRef, type CSSProperties } from "react";
import FbkCardShell from "./FbkCardShell";
import { ThemeOverlay } from "./ThemeOverlay";
import { resolveCardTheme } from "./theme";
import {
  defaultFooterLeft,
  resolveTextStyle,
  type CardFormat,
  type TeamOfWeekCardContent,
  type TeamOfWeekPlayerTile,
} from "./types";

interface TeamOfWeekCardProps {
  format: CardFormat;
  content: TeamOfWeekCardContent;
}

type Position = TeamOfWeekPlayerTile["position"];

const POSITION_ORDER: Position[] = ["FWD", "MID", "DEF", "GK"];

/**
 * Group an arbitrary player list into rows by position. Tolerates lists
 * that don't match a known formation (e.g. an edited lineup short on MIDs).
 */
function groupByPosition(
  players: TeamOfWeekPlayerTile[],
): Record<Position, TeamOfWeekPlayerTile[]> {
  const out: Record<Position, TeamOfWeekPlayerTile[]> = {
    GK: [],
    DEF: [],
    MID: [],
    FWD: [],
  };
  for (const p of players) {
    if (out[p.position]) out[p.position].push(p);
  }
  return out;
}

/** First letter of each word — used when a photo URL is missing or fails to load. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? "").join("");
}

const TeamOfWeekCard = forwardRef<HTMLDivElement, TeamOfWeekCardProps>(
  function TeamOfWeekCard({ format, content }, ref) {
    const isStory = format === "story";
    const style = content.style ?? {};
    const theme = resolveCardTheme(content.theme ?? "signature", {
      monoColor: content.monoColor,
    });

    const eyebrowStyle: CSSProperties = {
      ...resolveTextStyle(
        style.eyebrow,
        {
          fontFamily: theme.fontEyebrow,
          fontSizePx: 24,
          color: theme.accentColor,
          letterSpacing: "0.4em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      marginTop: isStory ? 40 : 24,
    };

    const titleDefaultSize = isStory ? 96 : 72;
    const titleStyle: CSSProperties = {
      ...resolveTextStyle(
        style.headline,
        {
          fontFamily: theme.fontHeadline,
          fontSizePx: titleDefaultSize,
          color: theme.headlineColor,
          letterSpacing: "-0.025em",
          bold: true,
        },
        format,
      ),
      lineHeight: 0.95,
      marginTop: isStory ? 12 : 8,
    };

    const formationPillStyle: CSSProperties = {
      ...resolveTextStyle(
        style.goldHighlight,
        {
          fontFamily: theme.fontBody,
          fontSizePx: isStory ? 26 : 22,
          color: theme.accentColor,
          letterSpacing: "0.2em",
          bold: true,
        },
        format,
      ),
      textTransform: "uppercase",
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: `${isStory ? 10 : 8}px ${isStory ? 22 : 18}px`,
      borderRadius: 9999,
      border: `1px solid ${theme.ringColor}55`,
      background: "rgba(255,255,255,0.08)",
      marginTop: isStory ? 16 : 10,
      // Lock to one line — the 0.2em letter-spacing pushes "4-3-3" wide
      // enough that it wraps under the parent's width without this.
      whiteSpace: "nowrap",
    };

    const taglineStyle: CSSProperties | null = content.tagline
      ? {
          ...resolveTextStyle(
            style.tagline,
            {
              fontFamily: theme.fontBody,
              fontSizePx: isStory ? 26 : 20,
              color: theme.subtleTextColor,
              italic: true,
              bold: false,
            },
            format,
          ),
          marginTop: isStory ? 18 : 12,
          maxWidth: isStory ? 880 : 760,
          lineHeight: 1.3,
        }
      : null;

    // Grouped + row layout — pitch is rendered as 4 rows (FWD top, MID, DEF, GK bottom).
    const rows = groupByPosition(content.players);

    // Pitch sizing — make it tall on Story, more compact on Post
    const pitchHeight = isStory ? 1100 : 740;
    const tileSize = isStory ? 132 : 100;
    const nameFontSize = isStory ? 22 : 17;
    const pointsFontSize = isStory ? 20 : 16;
    const clubCrestSize = isStory ? 38 : 30;
    const captainBadgeSize = isStory ? 34 : 26;

    const totalPointsLabelSize = isStory ? 18 : 14;
    const totalPointsNumberSize = isStory ? 60 : 44;

    return (
      <FbkCardShell
        ref={ref}
        format={format}
        footerLeft={content.footerLeft || defaultFooterLeft("TEAM_OF_WEEK")}
        footerRight={content.hashtags || "#WC2026 · #FBK"}
        header={content.header}
        background={style.background}
        customBaseGradient={theme.customBaseGradient}
        headerOnLight={theme.headerOnLight}
        suppressBrandOverlays={theme.suppressBrandOverlays}
        hideFooter={content.footer?.hide}
      >
        <ThemeOverlay theme={theme} format={format} />

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
            position: "relative",
            zIndex: 2,
          }}
        >
          {/* TITLE BLOCK */}
          <div style={eyebrowStyle}>
            {(content.eyebrow || "TEAM OF THE WEEK").toUpperCase()}
          </div>
          <div style={titleStyle}>{content.gameweekName || "Matchweek"}</div>
          <div>
            <div style={formationPillStyle}>{content.formation}</div>
          </div>
          {taglineStyle && <div style={taglineStyle}>{content.tagline}</div>}

          {/* PITCH */}
          <div
            style={{
              marginTop: isStory ? 32 : 20,
              flex: 1,
              minHeight: pitchHeight,
              position: "relative",
              borderRadius: isStory ? 28 : 22,
              overflow: "hidden",
              boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
              border: "1px solid rgba(255,255,255,0.12)",
              // Pitch base: alternating mowing stripes in two greens
              background: [
                "repeating-linear-gradient(180deg, #11823b 0px, #11823b 80px, #0f7335 80px, #0f7335 160px)",
              ].join(", "),
            }}
          >
            {/* Pitch markings overlay (center circle + halfway + boxes) */}
            <PitchMarkings isStory={isStory} />

            {/* Subtle dark vignette for tile legibility */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.35) 100%)",
                pointerEvents: "none",
              }}
            />

            {/* PLAYER ROWS */}
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                padding: `${isStory ? 56 : 40}px ${isStory ? 36 : 28}px`,
                zIndex: 2,
              }}
            >
              {POSITION_ORDER.map((pos) => {
                const rowPlayers = rows[pos];
                if (rowPlayers.length === 0) return <div key={pos} />;
                return (
                  <div
                    key={pos}
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                      gap: isStory ? 18 : 12,
                    }}
                  >
                    {rowPlayers.map((p, i) => (
                      <PlayerTile
                        key={`${pos}-${i}`}
                        player={p}
                        tileSize={tileSize}
                        nameFontSize={nameFontSize}
                        pointsFontSize={pointsFontSize}
                        clubCrestSize={clubCrestSize}
                        captainBadgeSize={captainBadgeSize}
                        accentColor={theme.accentColor}
                      />
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          {/* TOTAL POINTS STRIP */}
          <div
            style={{
              marginTop: isStory ? 28 : 18,
              padding: `${isStory ? 18 : 14}px ${isStory ? 28 : 22}px`,
              borderRadius: isStory ? 22 : 18,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.16)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                fontFamily: theme.fontEyebrow,
                fontSize: totalPointsLabelSize,
                fontWeight: 800,
                letterSpacing: "0.3em",
                textTransform: "uppercase",
                color: theme.subtleTextColor,
              }}
            >
              Total points
            </div>
            <div
              style={{
                fontFamily: theme.fontHeadline,
                fontSize: totalPointsNumberSize,
                fontWeight: 800,
                color: theme.accentColor,
                letterSpacing: "-0.02em",
                lineHeight: 1,
              }}
            >
              {content.totalPoints}
              <span
                style={{
                  fontSize: totalPointsLabelSize,
                  marginLeft: 8,
                  color: theme.subtleTextColor,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                pts
              </span>
            </div>
          </div>
        </div>
      </FbkCardShell>
    );
  },
);

export default TeamOfWeekCard;

// ─── Subcomponents ───────────────────────────────────────────────────────────

function PitchMarkings({ isStory }: { isStory: boolean }) {
  const stroke = "rgba(255,255,255,0.55)";
  const strokeWidth = isStory ? 3 : 2;
  return (
    <svg
      viewBox="0 0 1000 1400"
      preserveAspectRatio="none"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 1,
      }}
    >
      {/* Outer border */}
      <rect
        x="40"
        y="40"
        width="920"
        height="1320"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      {/* Halfway line */}
      <line
        x1="40"
        y1="700"
        x2="960"
        y2="700"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      {/* Center circle */}
      <circle
        cx="500"
        cy="700"
        r="120"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <circle cx="500" cy="700" r="6" fill={stroke} />
      {/* Top box */}
      <rect
        x="280"
        y="40"
        width="440"
        height="160"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <rect
        x="380"
        y="40"
        width="240"
        height="60"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      {/* Bottom box */}
      <rect
        x="280"
        y="1200"
        width="440"
        height="160"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
      <rect
        x="380"
        y="1300"
        width="240"
        height="60"
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
      />
    </svg>
  );
}

interface PlayerTileProps {
  player: TeamOfWeekPlayerTile;
  tileSize: number;
  nameFontSize: number;
  pointsFontSize: number;
  clubCrestSize: number;
  captainBadgeSize: number;
  accentColor: string;
}

function PlayerTile({
  player,
  tileSize,
  nameFontSize,
  pointsFontSize,
  clubCrestSize,
  captainBadgeSize,
  accentColor,
}: PlayerTileProps) {
  const photoSrc = player.photoUrl || undefined;
  const initials = initialsOf(player.name);
  const useRemoteCrest =
    !!player.clubLogoUrl && !player.clubLogoUrl.startsWith("data:");
  const useRemotePhoto =
    !!photoSrc && !photoSrc.startsWith("data:") && !photoSrc.startsWith("/");

  // Nation flag fallback via flagcdn when we have a code but no embedded URL
  const flagSrc =
    player.nationFlagUrl ||
    (player.nationCode
      ? `https://flagcdn.com/w160/${player.nationCode.toLowerCase()}.png`
      : undefined);
  const useRemoteFlag = !!flagSrc && !flagSrc.startsWith("data:");

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        width: tileSize + 20,
        gap: 6,
      }}
    >
      {/* Photo well */}
      <div
        style={{
          position: "relative",
          width: tileSize,
          height: tileSize,
        }}
      >
        <div
          style={{
            width: tileSize,
            height: tileSize,
            borderRadius: 9999,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.85) 100%)",
            border: `3px solid ${accentColor}`,
            boxShadow: "0 10px 24px rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          {photoSrc ? (
            <img
              src={photoSrc}
              alt={player.name}
              crossOrigin={useRemotePhoto ? "anonymous" : undefined}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <div
              style={{
                fontFamily: "'Bebas Neue', 'Anton', sans-serif",
                fontSize: tileSize * 0.45,
                fontWeight: 800,
                color: "#FFFFFF",
                letterSpacing: "0.05em",
              }}
            >
              {initials}
            </div>
          )}
        </div>

        {/* Captain armband badge */}
        {player.isCaptain && (
          <div
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              width: captainBadgeSize,
              height: captainBadgeSize,
              borderRadius: 9999,
              background: accentColor,
              color: "#0A4724",
              border: "2px solid #FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Bebas Neue', 'Anton', sans-serif",
              fontSize: captainBadgeSize * 0.7,
              fontWeight: 800,
              boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
            }}
            title="Captain"
          >
            C
          </div>
        )}

        {/* Club crest tucked bottom-left */}
        {player.clubLogoUrl && (
          <img
            src={player.clubLogoUrl}
            alt={player.clubName || ""}
            crossOrigin={useRemoteCrest ? "anonymous" : undefined}
            style={{
              position: "absolute",
              bottom: -4,
              left: -4,
              width: clubCrestSize,
              height: clubCrestSize,
              borderRadius: 8,
              objectFit: "cover",
              background: "#FFFFFF",
              padding: 3,
              border: "2px solid #FFFFFF",
              boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
            }}
          />
        )}

        {/* Nation flag tucked bottom-right */}
        {flagSrc && (
          <img
            src={flagSrc}
            alt={player.nationCode || ""}
            crossOrigin={useRemoteFlag ? "anonymous" : undefined}
            style={{
              position: "absolute",
              bottom: -4,
              right: -4,
              width: clubCrestSize * 0.95,
              height: clubCrestSize * 0.7,
              borderRadius: 4,
              objectFit: "cover",
              border: "2px solid #FFFFFF",
              boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
            }}
          />
        )}
      </div>

      {/* Name */}
      <div
        style={{
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: nameFontSize,
          fontWeight: 800,
          color: "#FFFFFF",
          letterSpacing: "-0.01em",
          lineHeight: 1.1,
          maxWidth: tileSize + 16,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          textShadow: "0 1px 4px rgba(0,0,0,0.7)",
        }}
      >
        {player.name}
      </div>

      {/* Points pill */}
      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: `2px 10px`,
          borderRadius: 9999,
          background: accentColor,
          color: "#0A4724",
          fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
          fontSize: pointsFontSize,
          fontWeight: 800,
          lineHeight: 1.2,
          letterSpacing: "0.02em",
          boxShadow: "0 3px 8px rgba(0,0,0,0.3)",
        }}
      >
        {player.points}
        <span style={{ fontSize: pointsFontSize * 0.6, opacity: 0.85 }}>
          PTS
        </span>
      </div>
    </div>
  );
}
